import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  startAfter,
  getDocs,
  getDoc,
  updateDoc,
  writeBatch,
  deleteDoc,
  DocumentSnapshot,
  QuerySnapshot,
  Unsubscribe,
  setDoc,
  documentId,
} from 'firebase/firestore';
import { auth, firestore } from '../utils/firebaseSetUp';
import { COLLECTIONS, SUBCOLLECTION_NAMES } from '../utils/collections';
import {
  SalesPerson,
  SalesPersonCreate,
  SalesPersonUpdate,
  SalesPersonFilters,
  SalesPersonQueryResult,
  SALES_PERSON_DEFAULTS,
} from '../models/sales_person';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import type { Client } from '../models/client';
import { isSelfHosted } from '../config/infra';
import { getSalesPeopleServer } from './salesPeopleServer';
import { TABLE_PAGE_SIZE } from './listPagination';
import {
  createApiListCursor,
  getApiListOffset,
} from '../utils/apiListCursor';
import { createPollingSubscription } from '../utils/pollingSubscription';

const salesPeopleCollectionRef = (organizationId: string) =>
  collection(
    firestore,
    COLLECTIONS.ORGANIZATIONS,
    organizationId,
    COLLECTIONS.SALES_PEOPLE
  );

const normalizeSalesPersonData = (
  id: string,
  data: Record<string, unknown>
): SalesPerson => {
  const mergedProfit: Record<string, unknown> = {};
  [
    data.profit_plus_2k8,
    data.profitplus2K8,
    data.profitplus2k8,
    data.profitPlus2k8,
  ].forEach(candidate => {
    if (
      candidate != null &&
      typeof candidate === 'object' &&
      !Array.isArray(candidate)
    ) {
      Object.assign(mergedProfit, candidate as Record<string, unknown>);
    }
  });

  const normalized: Record<string, unknown> = { ...data };
  delete normalized.profit_plus_2k8;
  delete normalized.profitplus2K8;
  delete normalized.profitplus2k8;
  if (Object.keys(mergedProfit).length > 0) {
    normalized.profitPlus2k8 = mergedProfit;
  }
  return { id, ...normalized } as SalesPerson;
};

/** Single sales person fetch (e.g. polling Profit sync status after queueing export). */
export async function fetchSalesPersonById(
  organizationId: string,
  salesPersonId: string
): Promise<SalesPerson | null> {
  const ref = doc(
    firestore,
    COLLECTIONS.ORGANIZATIONS,
    organizationId,
    COLLECTIONS.SALES_PEOPLE,
    salesPersonId
  );
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return normalizeSalesPersonData(
    snap.id,
    snap.data() as Record<string, unknown>
  );
}

/**
 * Resolve a stored `creatorSalesPerson` value (document id, email, code, or exact name)
 * to a {@link SalesPerson} with at most a few targeted reads.
 */
export async function fetchSalesPersonByCreatorReference(
  organizationId: string,
  stored: string
): Promise<SalesPerson | null> {
  const s = (stored ?? '').trim();
  if (!s) return null;

  const col = salesPeopleCollectionRef(organizationId);

  const byId = await getDoc(doc(col, s));
  if (byId.exists()) {
    return normalizeSalesPersonData(
      byId.id,
      byId.data() as Record<string, unknown>
    );
  }

  const firstMatch = async (
    field: 'email' | 'code' | 'name',
    value: string
  ): Promise<SalesPerson | null> => {
    const q = query(col, where(field, '==', value), limit(1));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return normalizeSalesPersonData(d.id, d.data() as Record<string, unknown>);
  };

  if (s.includes('@')) {
    let sp = await firstMatch('email', s);
    if (!sp) sp = await firstMatch('email', s.toLowerCase());
    if (sp) return sp;
  }

  let sp = await firstMatch('code', s);
  if (sp) return sp;

  sp = await firstMatch('name', s);
  if (sp) return sp;

  return null;
}

function filterSalesPeople(
  all: SalesPerson[],
  filters: SalesPersonFilters
): SalesPerson[] {
  let rows = [...all];
  if (filters.active !== undefined) {
    rows = rows.filter(sp => sp.active === filters.active);
  }
  if (filters.approvalState) {
    rows = rows.filter(sp => sp.approvalState === filters.approvalState);
  }
  if (filters.salesZone) {
    rows = rows.filter(sp => {
      const zones = (sp as SalesPerson & { salesZones?: string[] }).salesZones;
      return Array.isArray(zones) ? zones.includes(filters.salesZone!) : false;
    });
  }
  return rows;
}

async function fetchSalesPeoplePageSelfHosted(
  organizationId: string,
  filters: SalesPersonFilters,
  lastDocument: DocumentSnapshot | null | undefined,
  pageSize: number = TABLE_PAGE_SIZE
): Promise<SalesPersonQueryResult> {
  const offset = getApiListOffset(lastDocument, 0);
  const searchTerm = filters.searchTerm?.trim() || undefined;
  const page = await getSalesPeopleServer(organizationId, {
    skip: offset,
    limit: pageSize,
    searchTerm,
    sortField: 'created',
    sortDirection: -1,
  });
  const filtered = filterSalesPeople(page.items, filters);
  const nextOffset = offset + pageSize;
  return {
    salesPeople: filtered,
    hasMore: page.hasMore,
    lastDoc:
      page.hasMore
        ? (createApiListCursor(nextOffset) as unknown as DocumentSnapshot)
        : null,
  };
}

/** Load sales people once (REST API). */
export const loadSalesPeople = async (
  organizationId: string,
  filters: SalesPersonFilters,
  lastDocument: DocumentSnapshot | null = null,
  pageSize: number = 10
): Promise<SalesPersonQueryResult> => {
  return fetchSalesPeoplePageSelfHosted(
    organizationId,
    filters,
    lastDocument,
    pageSize
  );
};

// Get sales people with real-time updates
export const subscribeToSalesPeople = (
  organizationId: string,
  filters: SalesPersonFilters,
  onSuccess: (result: SalesPersonQueryResult) => void,
  onError: (error: Error) => void
): Unsubscribe => {
  if (isSelfHosted) {
    return createPollingSubscription(async () => {
      try {
        const result = await fetchSalesPeoplePageSelfHosted(
          organizationId,
          filters,
          null,
          10
        );
        onSuccess(result);
      } catch (error) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  const salesPeopleCollection = collection(
    firestore,
    COLLECTIONS.ORGANIZATIONS,
    organizationId,
    COLLECTIONS.SALES_PEOPLE
  );

  let q = query(salesPeopleCollection, orderBy('created', 'desc'), limit(10));

  if (filters.searchTerm && filters.searchTerm !== '') {
    q = query(
      salesPeopleCollection,
      where('keywords', 'array-contains', filters.searchTerm.toLowerCase()),
      orderBy('created', 'desc'),
      limit(10)
    );
  }

  if (filters.active !== undefined) {
    q = query(q, where('active', '==', filters.active));
  }

  if (filters.approvalState) {
    q = query(q, where('approvalState', '==', filters.approvalState));
  }

  if (filters.salesZone) {
    q = query(q, where('salesZones', 'array-contains', filters.salesZone));
  }

  return onSnapshot(
    q,
    (snapshot: QuerySnapshot) => {
      const salesPeople: SalesPerson[] = [];
      snapshot.forEach(doc => {
        const data = doc.data() as Record<string, unknown>;
        salesPeople.push(normalizeSalesPersonData(doc.id, data));
      });

      const result: SalesPersonQueryResult = {
        salesPeople,
        hasMore: snapshot.docs.length >= 10,
        lastDoc:
          snapshot.docs.length > 0
            ? snapshot.docs[snapshot.docs.length - 1]
            : null,
      };

      onSuccess(result);
    },
    error => {
      onError(error);
    }
  );
};

// Load more sales people
export const loadMoreSalesPeople = async (
  organizationId: string,
  filters: SalesPersonFilters,
  lastDocument: DocumentSnapshot
): Promise<SalesPersonQueryResult> => {
  return loadSalesPeople(organizationId, filters, lastDocument, 10);
};

// Create a new sales person
export const createSalesPerson = async (
  organizationId: string,
  salesPersonData: SalesPersonCreate
): Promise<SalesPerson> => {
  const salesPeopleCollection = collection(
    firestore,
    COLLECTIONS.ORGANIZATIONS,
    organizationId,
    COLLECTIONS.SALES_PEOPLE
  );

  const docRef = doc(salesPeopleCollection);
  const now = new Date();

  // TODO: here the user must be created from the server

  const batch = writeBatch(firestore);

  const userRef = doc(firestore, COLLECTIONS.USERS, docRef.id);

  batch.set(userRef, {
    uid: docRef.id,
    email: salesPersonData.email,
    role: 'salesPerson',
    organizationIds: [organizationId],
    emailVerified: false,
    phone: salesPersonData.phone || null,
    displayName: salesPersonData.name,
    photoURL: salesPersonData.imageUrl || null,
    active: true,
    created: now,
    updated: now,
  });

  const salesPersonDoc: SalesPerson = {
    id: docRef.id,
    ...SALES_PERSON_DEFAULTS,
    ...salesPersonData,
    created: now,
    updated: now,
  };

  batch.set(docRef, salesPersonDoc);

  await batch.commit();

  return salesPersonDoc;
};

// Update an existing sales person
export const updateSalesPerson = async (
  organizationId: string,
  salesPersonId: string,
  updates: SalesPersonUpdate
): Promise<void> => {
  const salesPersonRef = doc(
    firestore,
    COLLECTIONS.ORGANIZATIONS,
    organizationId,
    COLLECTIONS.SALES_PEOPLE,
    salesPersonId
  );

  await updateDoc(salesPersonRef, {
    ...updates,
    updated: new Date(),
  });
};

// Delete a sales person
export const deleteSalesPerson = async (
  organizationId: string,
  salesPersonId: string
): Promise<void> => {
  const salesPersonRef = doc(
    firestore,
    COLLECTIONS.ORGANIZATIONS,
    organizationId,
    COLLECTIONS.SALES_PEOPLE,
    salesPersonId
  );

  await deleteDoc(salesPersonRef);
};

// Update sales person active status
export const updateSalesPersonActiveStatus = async (
  organizationId: string,
  salesPersonId: string,
  active: boolean
): Promise<void> => {
  const salesPersonRef = doc(
    firestore,
    COLLECTIONS.ORGANIZATIONS,
    organizationId,
    COLLECTIONS.SALES_PEOPLE,
    salesPersonId
  );

  await updateDoc(salesPersonRef, {
    active,
    updated: new Date(),
  });
};

// Update sales person approval state
export const updateSalesPersonApprovalState = async (
  organizationId: string,
  salesPersonId: string,
  approvalState: 'approved' | 'pending' | 'rejected'
): Promise<void> => {
  const salesPersonRef = doc(
    firestore,
    COLLECTIONS.ORGANIZATIONS,
    organizationId,
    COLLECTIONS.SALES_PEOPLE,
    salesPersonId
  );

  await updateDoc(salesPersonRef, {
    approvalState,
    updated: new Date(),
  });
};

// Set sales person priority
export const setSalesPersonPriority = async (
  organizationId: string,
  salesPersonId: string,
  priority: boolean
): Promise<void> => {
  const salesPersonRef = doc(
    firestore,
    COLLECTIONS.ORGANIZATIONS,
    organizationId,
    COLLECTIONS.SALES_PEOPLE,
    salesPersonId
  );

  await updateDoc(salesPersonRef, {
    priority,
    updated: new Date(),
  });
};

// Update sales person performance metrics
export const updateSalesPersonPerformance = async (
  organizationId: string,
  salesPersonId: string,
  metrics: {
    salesCount?: number;
    totalSales?: number;
    currentSales?: number;
    objectiveSales?: number;
    realSales?: number;
  }
): Promise<void> => {
  const salesPersonRef = doc(
    firestore,
    COLLECTIONS.ORGANIZATIONS,
    organizationId,
    COLLECTIONS.SALES_PEOPLE,
    salesPersonId
  );

  await updateDoc(salesPersonRef, {
    ...metrics,
    updated: new Date(),
  });
};

const chunkArray = <T>(items: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }
  return chunks;
};

const salesPersonClientsCollection = (
  organizationId: string,
  salesPersonId: string
) =>
  collection(
    firestore,
    COLLECTIONS.ORGANIZATIONS,
    organizationId,
    COLLECTIONS.SALES_PEOPLE,
    salesPersonId,
    SUBCOLLECTION_NAMES.SALES_PERSON_CLIENTS
  );

/**
 * Assign a customer (client) to a sales person by creating/updating a mapping doc:
 * `organizations/{orgId}/salesPeople/{salesPersonId}/clients/{customerId}`
 */
export const assignCustomerToSalesPerson = async (
  organizationId: string,
  salesPersonId: string,
  customerId: string,
  assignedBy: string
): Promise<void> => {
  const customersRef = salesPersonClientsCollection(
    organizationId,
    salesPersonId
  );
  const mappingRef = doc(customersRef, customerId);

  await setDoc(
    mappingRef,
    {
      active: true,
      assignedAt: new Date(),
      assignedBy,
      updated: new Date(),
    },
    { merge: true }
  );
};

/**
 * Unassign a customer (client) from a sales person by deleting the mapping doc.
 */
export const unassignCustomerFromSalesPerson = async (
  organizationId: string,
  salesPersonId: string,
  customerId: string
): Promise<void> => {
  const customersRef = salesPersonClientsCollection(
    organizationId,
    salesPersonId
  );
  await deleteDoc(doc(customersRef, customerId));
};

/**
 * List customer ids assigned to a sales person (mapping doc ids).
 */
export const listCustomerIdsForSalesPerson = async (
  organizationId: string,
  salesPersonId: string
): Promise<string[]> => {
  const customersRef = salesPersonClientsCollection(
    organizationId,
    salesPersonId
  );
  const snap = await getDocs(customersRef);
  return snap.docs.map(d => d.id);
};

/**
 * Load customer (client) documents assigned to a sales person.
 *
 * Note: uses chunked `in` queries on documentId() to stay within Firestore limits.
 */
export const listCustomersForSalesPerson = async (
  organizationId: string,
  salesPersonId: string
): Promise<Client[]> => {
  const customerIds = await listCustomerIdsForSalesPerson(
    organizationId,
    salesPersonId
  );
  if (customerIds.length === 0) return [];

  const clientsRef = collection(
    firestore,
    COLLECTIONS.ORGANIZATIONS,
    organizationId,
    COLLECTIONS.CLIENTS
  );

  const byId = new Map<string, Client>();

  for (const chunk of chunkArray(customerIds, 10)) {
    const q = query(clientsRef, where(documentId(), 'in', chunk));
    const snap = await getDocs(q);
    snap.forEach(docSnap => {
      byId.set(docSnap.id, {
        id: docSnap.id,
        ...docSnap.data(),
      } as Client);
    });
  }

  // Preserve mapping order as best as possible
  return customerIds.map(id => byId.get(id)).filter(Boolean) as Client[];
};
